import React, { useState } from 'react';
import { GeoLevel } from '../../types/database';

const GEO_LEVEL_LABELS: Record<GeoLevel, string> = {
  [GeoLevel.NATIONAL]: 'ရုံးချုပ် (National)',
  [GeoLevel.STATE]: 'ပြည်နယ်/တိုင်း (State/Region)',
  [GeoLevel.DISTRICT]: 'ခရိုင် (District)',
  [GeoLevel.TOWNSHIP]: 'မြို့နယ် (Township)',
};

// Helper function to normalize targetGeoLevels prop into a GeoLevel array
const normalizeGeoLevels = (
  input?: GeoLevel[] | string[] | string
): GeoLevel[] => {
  if (!input) return [];
  if (Array.isArray(input)) return input as GeoLevel[];
  if (typeof input === 'string') {
    return input
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean) as GeoLevel[];
  }
  return [];
};

export interface FormActionButtonsProps {
  isPreview: boolean;
  isEditable: boolean;
  isSubmitting: boolean;
  isFormSigned: boolean;
  isApproved?: boolean;
  currentGeoLevel?: GeoLevel;
  targetGeoLevels?: GeoLevel[] | string[] | string; // 👈 Accepts array or string from formData
  selectedGeoLevels?: GeoLevel[];
  onGeoLevelChange?: (levels: GeoLevel[]) => void;
  onCancel: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onSaveDraft: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  onApproveAndSave: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  onSubmitToSuperior: (
    e: React.MouseEvent<HTMLButtonElement>,
    targetLevels?: GeoLevel[]
  ) => void | Promise<void>;
}

export const FormActionButtons: React.FC<FormActionButtonsProps> = ({
  isPreview,
  isEditable,
  isSubmitting,
  isFormSigned,
  isApproved = false,
  currentGeoLevel,
  targetGeoLevels,
  selectedGeoLevels: controlledGeoLevels,
  onGeoLevelChange,
  onCancel,
  onSaveDraft,
  onApproveAndSave,
  onSubmitToSuperior,
}) => {
  const [internalGeoLevels, setInternalGeoLevels] = useState<GeoLevel[]>([]);

  // Existing target levels from formData
  const existingLevels = normalizeGeoLevels(targetGeoLevels);

  const currentGeoLevels =
    controlledGeoLevels !== undefined ? controlledGeoLevels : internalGeoLevels;

  // Combined selected levels (Existing + Newly Checked)
  const allSelectedLevels = Array.from(
    new Set([...existingLevels, ...currentGeoLevels])
  );

  // Filter out the user's current geo level
  const availableTargetLevels = Object.values(GeoLevel).filter(
    (level) => level !== currentGeoLevel
  );

  const handleLevelToggle = (level: GeoLevel) => {
    // Prevent toggling if already fixed in targetGeoLevels
    if (existingLevels.includes(level)) return;

    const nextLevels = currentGeoLevels.includes(level)
      ? currentGeoLevels.filter((l) => l !== level)
      : [...currentGeoLevels, level];

    if (onGeoLevelChange) {
      onGeoLevelChange(nextLevels);
    } else {
      setInternalGeoLevels(nextLevels);
    }
  };

  if (isPreview) return null;

  return (
    <div className="flex flex-col gap-4 pt-6 mt-6 border-t border-slate-100 print:hidden">
      {/* Target GeoLevel Selection (Checkbox array) */}
      {isFormSigned && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
          <span className="text-xs font-semibold text-slate-700">
            တင်ပြလိုသည့် အဆင့်များ ရွေးချယ်ပါ:
          </span>
          <div className="flex flex-wrap items-center gap-3">
            {availableTargetLevels.map((level) => {
              const isExisting = existingLevels.includes(level);
              const isChecked = isExisting || currentGeoLevels.includes(level);

              return (
                <label
                  key={level}
                  className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${
                    isExisting
                      ? 'border-slate-300 bg-slate-200/60 text-slate-500 opacity-60 cursor-not-allowed select-none' // Dimmed & disabled style
                      : isChecked
                      ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold cursor-pointer'
                      : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100 cursor-pointer'
                  }`}
                >
                  <input
                    type="checkbox"
                    value={level}
                    checked={isChecked}
                    onChange={() => handleLevelToggle(level)}
                    disabled={isSubmitting || isExisting} // Disable if existing or submitting
                    className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {GEO_LEVEL_LABELS[level]}
                  {isExisting && (
                    <span className="text-[10px] text-slate-400 ml-0.5">(တင်ပြပြီး)</span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ထွက်မည်
        </button>

        {isEditable && !isFormSigned && (
          <button
            type="button"
            onClick={onSaveDraft}
            disabled={isSubmitting}
            className="rounded-lg border border-slate-300 bg-slate-100 px-5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? '💾 သိမ်းဆည်းနေသည်...' : '💾 မူကြမ်းသိမ်းဆည်းမည် (Save Draft)'}
          </button>
        )}

        {isFormSigned && (
          <>
            {!isApproved && isEditable && (
              <button
                type="button"
                onClick={onApproveAndSave}
                disabled={isSubmitting}
                className="rounded-lg bg-emerald-600 px-5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? '⏳ လုပ်ဆောင်နေသည်...' : '✅ အတည်ပြုသိမ်းဆည်းမည် (Approve & Save)'}
              </button>
            )}

            <button
              type="button"
              onClick={(e) => onSubmitToSuperior(e, allSelectedLevels)}
              disabled={isSubmitting}
              className="rounded-lg bg-blue-600 px-5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? '⏳ လုပ်ဆောင်နေသည်...' : '🚀 အထက်သို့တင်ပြမည် (Send Up)'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};